import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { UserGuard } from '@common/interfaces';
import { AddAccessoryModalComponent } from 'libs/shared/ui-components/src/lib/add-accessory-modal/add-accessory-modal.component';
import { AddAssetModalComponent } from 'libs/shared/ui-components/src/lib/add-asset-modal/add-asset-modal.component';
import { PrivacyPolicyComponent } from 'libs/shared/ui-components/src/lib/privacy-policy/privacy-policy.component';
import { TermsAndConditionsComponent } from 'libs/shared/ui-components/src/lib/terms-and-conditions/terms-and-conditions.component';
import { ViewTicketModalComponent } from 'libs/shared/ui-components/src/lib/view-ticket-modal/view-ticket-modal.component';
import { AuthService } from '../auth/auth.service';
import { AdminApprovalsPersonalComponent } from './admin/admin-approvals-personal/admin-approvals-personal/admin-approvals-personal.component';
import { AdminApprovalsComponent } from './admin/admin-approvals/admin-approvals/admin-approvals.component';
import { AdminBulkUploadComponent } from './admin/admin-bulk-upload/admin-bulk-upload.component';
import { AdminInventoryComponent } from './admin/admin-inventory/admin-inventory.component';
import { AdminTicketPersonalComponent } from './admin/admin-ticket-personal/admin-ticket-personal.component';
import { AdminTicketComponent } from './admin/admin-ticket/admin-ticket.component';
import { AssetDetailsComponent } from './admin/asset-details/asset-details.component';
import { AssetTeamDashboardPersonalComponent } from './admin/asset-team-dashboard-personal/asset-team-dashboard-personal.component';
import { AssetTeamDashboardComponent } from './admin/asset-team-dashboard/asset-team-dashboard.component';
import { EmployeeDetailsComponent } from './admin/employee-details/employee-details.component';
import { HomeComponent } from './dashboard/home.component';
import { EmployeeApprovalComponent } from './employee/employee-approval/employee-approval.component';
import { EmployeeDashboardComponent } from './employee/employee-dashboard/employee-dashboard.component';
import { WelcomeComponent } from './employee/welcome/welcome.component';
import { CreateSubsciptionPlanComponent } from './super-admin/create-subsciption-plan/create-subsciption-plan.component';
import { CreateWorkspaceComponent } from './super-admin/create-workspace/create-workspace.component';
import { EditWorkspaceComponent } from './super-admin/edit-workspace/edit-workspace.component';
import { SubscriptionPlanComponent } from './super-admin/subscription-plan/subscription-plan.component';
import { SuperAdminDashboardComponent } from './super-admin/super-admin-dashboard/super-admin-dashboard.component';
import { WorkspaceDetailsComponent } from './super-admin/workspace-details/workspace-details.component';
import { WorkspaceInsightsComponent } from './super-admin/workspace-insights/workspace-insights.component';
import { AccessoryTypeDetailComponent } from './workspace-owner/accessory-type-detail/accessory-type-detail.component';
import { AddAccessoryTypeComponent } from './workspace-owner/add-accessory-type/add-accessory-type.component';
import { AddAssetTypeComponent } from './workspace-owner/add-asset-type/add-asset-type.component';
import { AssetTypeDetailComponent } from './workspace-owner/asset-type-detail/asset-type-detail.component';
import { ManageWorkspaceOwnerComponent } from './workspace-owner/manage-workspace-owner/manage-workspace-owner.component';

const routes: Routes = [
  {
    path: 'home',
    component: HomeComponent,
    children: [
      //Employee Routes
      {
        path: 'welcome',
        component: WelcomeComponent,
        canActivate: [AuthService, UserGuard],
        data: { role: 'USER' },
      },
      {
        path: 'employee-dashboard',
        component: EmployeeDashboardComponent,
        canActivate: [AuthService, UserGuard],
        data: { role: 'USER' },
        children: [
          {
            path: 'ticket/:id',
            component: ViewTicketModalComponent,
            canActivate: [AuthService],
          },
        ],
      },
      {
        path: 'employee-approval',
        component: EmployeeApprovalComponent,
        canActivate: [AuthService, UserGuard],
        data: { role: 'USER' },
        children: [
          {
            path: 'ticket/:id',
            component: ViewTicketModalComponent,
            canActivate: [AuthService],
          },
        ],
      },
      {
        path: 'profile-details/:id',
        component: EmployeeDetailsComponent,
        canActivate: [AuthService],
        data: { role: 'USER' },
      },

      // Admin Routes
      {
        path: 'asset-team-dashboard',
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
        children: [
          {
            path: '',
            redirectTo: 'coditas',
            pathMatch: 'full',
          },
          { path: 'coditas', component: AssetTeamDashboardComponent, canActivate: [AuthService] },
          { path: 'personal', component: AssetTeamDashboardPersonalComponent, canActivate: [AuthService], },
        ],
      },
      {
        path: 'admin-ticket',
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
        children: [
          {
            path: '',
            redirectTo: 'coditas',
            pathMatch: 'full',
          },
          { path: 'coditas', component: AdminTicketComponent, canActivate: [AuthService], },
          { path: 'personal', component: AdminTicketPersonalComponent, canActivate: [AuthService], },
          {
            path: 'ticket/:id',
            component: ViewTicketModalComponent,
            canActivate: [AuthService],
          },
        ],
      },
      {
        path: 'admin-approval',
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
        children: [
          {
            path: '',
            redirectTo: 'coditas',
            pathMatch: 'full',
          },
          { path: 'coditas', component: AdminApprovalsComponent, canActivate: [AuthService], },
          { path: 'personal', component: AdminApprovalsPersonalComponent, canActivate: [AuthService], },
          {
            path: 'ticket/:id',
            component: ViewTicketModalComponent,
            canActivate: [AuthService],
          },
        ],
      },

      {
        path: 'inventory',
        component: AdminInventoryComponent,
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
        children: [
          {
            path: 'asset',
            component: AddAssetModalComponent,
          },
          {
            path: 'asset/:id',
            component: AddAssetModalComponent,
          },
          {
            path: 'asset-details/:id',
            component: AssetDetailsComponent,
          },
          {
            path: 'accessory',
            component: AddAccessoryModalComponent,
          },
        ],
      },
      {
        path: 'bulk-upload',
        component: AdminBulkUploadComponent,
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
      },
      {
        path: 'main',
        canActivate: [AuthService],
        data: { role: 'SUPER-ADMIN' },
        children: [
          {
            path: '',
            redirectTo: 'dashboard',
            pathMatch: 'full'
          },
          {
            path: 'dashboard',
            component: SuperAdminDashboardComponent,
          },
          {
            path: 'insights',
            component: WorkspaceInsightsComponent
          },
          {
            path: 'create-workspace',
            component: CreateWorkspaceComponent,
          },
          {
            path: 'workspace-details/:workspaceId',
            component: WorkspaceDetailsComponent
          },
          {
            path: 'edit-workspace/:workspaceId',
            component: EditWorkspaceComponent,
          },
          {
            path: 'subscription-plan',
            component: SubscriptionPlanComponent,
          },
          {
            path: 'create-subscription-plan',
            component: CreateSubsciptionPlanComponent,
          },
        ],
      },
      {
        path: 'manage-workspace-owner',
        component: ManageWorkspaceOwnerComponent,
        canActivate: [AuthService],
        data: { role: 'WORKSPACE_OWNER' },
        children: [
          {
            path: 'add-asset-type',
            component: AddAssetTypeComponent,
          },
          {
            path: 'asset-type/:id',
            component: AssetTypeDetailComponent,
          },
          {
            path: 'add-accessory-type',
            component: AddAccessoryTypeComponent,
          },
          {
            path: 'accessory-type/:id',
            component: AccessoryTypeDetailComponent,
          },
        ],
      },

      {
        path: 'employee-details/:id',
        component: EmployeeDetailsComponent,
        canActivate: [AuthService],
        data: { role: 'ADMIN' },
      },
      {
        path: 'bulk-upload', component: AdminBulkUploadComponent, canActivate: [AuthService],
      },

      //Common Routes
      { path: 'privacy-policy', component: PrivacyPolicyComponent },
      { path: 'terms-and-conditions', component: TermsAndConditionsComponent },
    ],
  },
  { path: '**', redirectTo: '404-not-found' },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class DashboardRoutingModule { }
